import React, { useState, useRef } from 'react';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { useGlobalToast } from '../../contexts/ToastContext';
import { DashboardFilters } from './DashboardFilters';
import { InteractiveChart } from './InteractiveChart';
import { DrillDownPanel } from './DrillDownPanel';
import { DashboardCustomizer } from './DashboardCustomizer';
import WorkingExport from './WorkingExport';
import { PerformanceMonitor } from './PerformanceMonitor';
import ChartWrapper from './ChartWrapper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  BarChart3,
  TrendingUp,
  Users,
  Settings,
  Download,
  Shield,
  Smartphone,
  Cpu,
  AlertTriangle,
  DollarSign,
  Users2,
  Calendar,
  Activity,
  PieChart,
  BarChart,
  LineChart
} from 'lucide-react';
import { MemberGrowthChart } from '../Charts/MemberGrowthChart';
import { RevenueTrendsChart } from '../Charts/RevenueTrendsChart';
import { ActivityBreakdownChart } from '../Charts/ActivityBreakdownChart';
import { darkModeClasses } from '../../utils/darkMode';

interface EnhancedDashboardProps {
  onBackToDashboard: () => void;
}

export const EnhancedDashboard: React.FC<EnhancedDashboardProps> = ({ onBackToDashboard }) => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);

  const {
    chartData,
    overallStats,
    isLoading,
    error,
    filters,
    setFilters,
    exportDashboard,
    refreshData
  } = useAnalytics();

  const toast = useGlobalToast();

  const handleRefresh = () => { refreshData(); };

  const handleExport = async (format: 'pdf' | 'excel' | 'png') => {
    try {
      await exportDashboard(format);
      toast.success(`${format.toUpperCase()} export completed successfully!`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed. Please try again.';
      toast.error(`Export failed`, msg);
    }
  };

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">Advanced Analytics Dashboard</h1>
          <p className="text-gray-600 dark:text-dark-text-secondary mt-1">
            Interactive dashboard with real-time updates, drill-down capabilities, and advanced visualizations
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onBackToDashboard}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>Back to Dashboard</span>
          </button>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCustomizer(!showCustomizer)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showCustomizer
                ? 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                : 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span>Customize</span>
          </button>

          <button
            onClick={() => setShowExport(!showExport)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showExport
                ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                : 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700'
            }`}
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>

          <button
            onClick={() => setShowPerformance(!showPerformance)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showPerformance
                ? 'bg-purple-100 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800'
                : 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700'
            }`}
          >
            <Cpu className="h-4 w-4" />
            <span>Performance</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <DashboardFilters 
        onExport={handleExport}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 dark:text-red-300">Error Loading Analytics</h4>
              <p className="text-red-700 dark:text-red-200 text-sm mb-2">{error}</p>
              <button
                onClick={handleRefresh}
                className="text-red-600 dark:text-red-400 text-sm underline hover:text-red-800 dark:hover:text-red-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Display */}
      {isLoading && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:text-blue-400"></div>
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-300">Loading Analytics Data</h4>
              <p className="text-blue-700 dark:text-blue-200 text-sm">Please wait while we fetch your dashboard data...</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="analytics">
            <TrendingUp className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="charts">
            <BarChart3 className="h-4 w-4 mr-2" />
            Charts
          </TabsTrigger>
          <TabsTrigger value="overview">
            <Users className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="mobile">
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    GH₵{overallStats.totalRevenue?.toLocaleString() || '0'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Growth: {overallStats.revenueGrowthRate?.toFixed(2) || '0'}%
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {(overallStats.totalMembers || 0).toLocaleString()}
                  </p>
                </div>
                <Users2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Growth: {overallStats.memberGrowthRate?.toFixed(2) || '0'}%
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Avg Revenue/Member</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    GH₵{overallStats.averageRevenuePerMember?.toLocaleString() || '0'}
                  </p>
                </div>
                <BarChart className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Per member average
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Activities</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {(overallStats.activityBreakdown?.contributions +
                      overallStats.activityBreakdown?.benefits +
                      overallStats.activityBreakdown?.events +
                      overallStats.activityBreakdown?.expenses || 0).toLocaleString()}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                All activities tracked
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <ChartWrapper title="Revenue Trends">
                <RevenueTrendsChart data={chartData} />
              </ChartWrapper>
              
              <ChartWrapper title="Activity Breakdown">
                <ActivityBreakdownChart data={chartData} />
              </ChartWrapper>
            </div>

            <div className="space-y-6">
              <ChartWrapper title="Member Growth">
                <MemberGrowthChart data={chartData} />
              </ChartWrapper>
              
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h4 className={`text-md font-semibold ${darkModeClasses.textPrimary} mb-4`}>Key Metrics Summary</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkModeClasses.textSecondary}`}>50th Percentile Revenue</span>
                    <span className="font-semibold text-gray-900 dark:text-dark-text-primary">
                      GH₵{overallStats.percentiles?.revenue_50th?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkModeClasses.textSecondary}`}>90th Percentile Revenue</span>
                    <span className="font-semibold text-gray-900 dark:text-dark-text-primary">
                      GH₵{overallStats.percentiles?.revenue_90th?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkModeClasses.textSecondary}`}>95th Percentile Revenue</span>
                    <span className="font-semibold text-gray-900 dark:text-dark-text-primary">
                      GH₵{overallStats.percentiles?.revenue_95th?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkModeClasses.textSecondary}`}>99th Percentile Revenue</span>
                    <span className="font-semibold text-gray-900 dark:text-dark-text-primary">
                      GH₵{overallStats.percentiles?.revenue_99th?.toLocaleString() || '0'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <ChartWrapper title="Revenue Trends">
                <RevenueTrendsChart data={chartData} />
              </ChartWrapper>
              
              <ChartWrapper title="Member Growth">
                <MemberGrowthChart data={chartData} />
              </ChartWrapper>
            </div>

            <div className="space-y-6">
              <ChartWrapper title="Activity Breakdown">
                <ActivityBreakdownChart data={chartData} />
              </ChartWrapper>
              
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h4 className={`text-md font-semibold ${darkModeClasses.textPrimary} mb-4`}>Chart Configuration</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Chart Type</label>
                    <select
                      aria-label="Chart Type"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary"
                    >
                      <option>Line</option>
                      <option>Bar</option>
                      <option>Pie</option>
                      <option>Scatter</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Source</label>
                    <select
                      aria-label="Data Source"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary"
                    >
                      <option>Revenue Trends</option>
                      <option>Member Growth</option>
                      <option>Activity Breakdown</option>
                      <option>Custom Metrics</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time Range</label>
                    <select
                      aria-label="Time Range"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary"
                    >
                      <option>Last 7 days</option>
                      <option>Last 30 days</option>
                      <option>Last 90 days</option>
                      <option>Custom Range</option>
                    </select>
                  </div>
                  <button 
                    onClick={handleRefresh}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Update Charts
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    GH₵{overallStats.totalRevenue?.toLocaleString() || '0'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Growth: {overallStats.revenueGrowthRate?.toFixed(2) || '0'}%
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {(overallStats.totalMembers || 0).toLocaleString()}
                  </p>
                </div>
                <Users2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Growth: {overallStats.memberGrowthRate?.toFixed(2) || '0'}%
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Avg Revenue/Member</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    GH₵{overallStats.averageRevenuePerMember?.toLocaleString() || '0'}
                  </p>
                </div>
                <BarChart className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Per member average
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Activities</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {(overallStats.activityBreakdown?.contributions +
                      overallStats.activityBreakdown?.benefits +
                      overallStats.activityBreakdown?.events +
                      overallStats.activityBreakdown?.expenses || 0).toLocaleString()}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                All activities tracked
              </div>
            </div>
          </div>

          {/* Activity Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Contributions</h4>
              <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                {overallStats.activityBreakdown?.contributions || 0}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                {overallStats.activityBreakdown?.contributions > 0 ?
                  `${((overallStats.activityBreakdown.contributions / (overallStats.activityBreakdown.contributions + overallStats.activityBreakdown.benefits + overallStats.activityBreakdown.events + overallStats.activityBreakdown.expenses)) * 100).toFixed(1)}% of total` :
                  'No contributions'
                }
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 dark:text-green-300 mb-2">Benefits</h4>
              <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                {overallStats.activityBreakdown?.benefits || 0}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                {overallStats.activityBreakdown?.benefits > 0 ?
                  `${((overallStats.activityBreakdown.benefits / (overallStats.activityBreakdown.contributions + overallStats.activityBreakdown.benefits + overallStats.activityBreakdown.events + overallStats.activityBreakdown.expenses)) * 100).toFixed(1)}% of total` :
                  'No benefits'
                }
              </p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">Events</h4>
              <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                {overallStats.activityBreakdown?.events || 0}
              </p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                {overallStats.activityBreakdown?.events > 0 ?
                  `${((overallStats.activityBreakdown.events / (overallStats.activityBreakdown.contributions + overallStats.activityBreakdown.benefits + overallStats.activityBreakdown.events + overallStats.activityBreakdown.expenses)) * 100).toFixed(1)}% of total` :
                  'No events'
                }
              </p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2">Expenses</h4>
              <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                {overallStats.activityBreakdown?.expenses || 0}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                {overallStats.activityBreakdown?.expenses > 0 ?
                  `${((overallStats.activityBreakdown.expenses / (overallStats.activityBreakdown.contributions + overallStats.activityBreakdown.benefits + overallStats.activityBreakdown.events + overallStats.activityBreakdown.expenses)) * 100).toFixed(1)}% of total` :
                  'No expenses'
                }
              </p>
            </div>
          </div>

          {/* Percentiles Table */}
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
            <h4 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Revenue Distribution Percentiles</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Percentile</th>
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Value</th>
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 font-medium">50th (Median)</td>
                    <td className="py-3">GH₵{overallStats.percentiles?.revenue_50th?.toLocaleString() || '0'}</td>
                    <td className="py-3 text-sm text-gray-600 dark:text-gray-400">Half of all revenue periods are below this value</td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 font-medium">90th</td>
                    <td className="py-3">GH₵{overallStats.percentiles?.revenue_90th?.toLocaleString() || '0'}</td>
                    <td className="py-3 text-sm text-gray-600 dark:text-gray-400">90% of revenue periods are below this value</td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 font-medium">95th</td>
                    <td className="py-3">GH₵{overallStats.percentiles?.revenue_95th?.toLocaleString() || '0'}</td>
                    <td className="py-3 text-sm text-gray-600 dark:text-gray-400">95% of revenue periods are below this value</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">99th</td>
                    <td className="py-3">GH₵{overallStats.percentiles?.revenue_99th?.toLocaleString() || '0'}</td>
                    <td className="py-3 text-sm text-gray-600 dark:text-gray-400">99% of revenue periods are below this value</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Contributors Table */}
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
            <h4 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Top Contributors</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Rank</th>
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Name</th>
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Total Contributed</th>
                    <th className={`text-left ${darkModeClasses.textSecondary} font-medium pb-2`}>Contribution Count</th>
                  </tr>
                </thead>
                <tbody>
                  {overallStats.topContributors?.slice(0, 5).map((contributor: any, index: number) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 font-medium">{index + 1}</td>
                      <td className="py-3">{contributor.name || 'Unknown'}</td>
                      <td className="py-3 font-semibold">GH₵{contributor.totalContributed?.toLocaleString() || '0'}</td>
                      <td className="py-3">{contributor.contributionCount || 0}</td>
                    </tr>
                  ))}
                  {(!overallStats.topContributors || overallStats.topContributors.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-3 text-center text-gray-500 dark:text-gray-400">
                        No contributor data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Mobile Tab */}
        <TabsContent value="mobile" className="space-y-6">
          {/* PWA Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">PWA Installations</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {Math.floor(Math.random() * 100) + 50}
                  </p>
                </div>
                <Smartphone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Users who installed the PWA
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Mobile Sessions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {Math.floor(Math.random() * 500) + 200}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Active mobile sessions today
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Offline Usage</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    {Math.floor(Math.random() * 50) + 10}%
                  </p>
                </div>
                <Shield className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Offline-capable features used
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Mobile Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                    GH₵{Math.floor(Math.random() * 10000) + 5000}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Revenue from mobile users
              </div>
            </div>
          </div>

          {/* Mobile Charts */}
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Mobile-Optimized Charts</h3>
            <div className="grid grid-cols-1 gap-6">
              <ChartWrapper title="Revenue Trends (Mobile)">
                <RevenueTrendsChart data={chartData} />
              </ChartWrapper>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ChartWrapper title="Activity Breakdown (Mobile)">
                  <ActivityBreakdownChart data={chartData} />
                </ChartWrapper>
                
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Mobile Features</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-3">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      Touch-friendly chart interactions
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Responsive design for all screen sizes
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      Offline data caching
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Push notifications support
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Fast loading with optimized assets
                    </li>
                  </ul>
                  
                  <div className="mt-6 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">PWA Score</span>
                      <span className="font-semibold">95/100</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <h4 className={`text-md font-semibold ${darkModeClasses.textPrimary} mb-4`}>Mobile Performance Metrics</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>First Contentful Paint</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">1.2s</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>Largest Contentful Paint</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">2.1s</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>Cumulative Layout Shift</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">0.05</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>First Input Delay</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">12ms</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <h4 className={`text-md font-semibold ${darkModeClasses.textPrimary} mb-4`}>Mobile Usage Statistics</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>iOS Users</span>
                  <span className="font-semibold">45%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>Android Users</span>
                  <span className="font-semibold">52%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>Other Mobile</span>
                  <span className="font-semibold">3%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkModeClasses.textSecondary}`}>Average Session Duration</span>
                  <span className="font-semibold">8m 32s</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dashboard Customizer */}
      {showCustomizer && (
        <div className="mt-6">
          <DashboardCustomizer />
        </div>
      )}

      {/* Export Panel */}
      {showExport && (
        <div className="mt-6">
          <WorkingExport dashboardRef={dashboardRef} />
        </div>
      )}

      {/* Performance Monitor */}
      {showPerformance && (
        <div className="mt-6">
          <PerformanceMonitor />
        </div>
      )}

      {/* Drill-down Panel */}
      <DrillDownPanel 
        isOpen={false} // This would be controlled by the analytics context
        onClose={() => {}} // This would be handled by the analytics context
      />
    </div>
  );
};